import React, { useState } from 'react';
import axiosInstance from '../../../utils/axios';
import toast from 'react-hot-toast';

// ============================================
// Component: CompanyChecksList
// Purpose: Display all checks for a specific company
// Features:
// - Show all checks from all payments
// - Filter by status
// - Summary stats
// - Update check status (admin only)
// ============================================

const CompanyChecksList = ({ payments, onUpdate }) => {
  const [filter, setFilter] = useState('all'); // all, pending, cleared, bounced
  const [updatingCheckId, setUpdatingCheckId] = useState(null);

  // Extract all checks from all payments
  const allChecks = payments
    ?.filter(p => p.checks && p.checks.length > 0)
    .flatMap(payment => 
      payment.checks.map(check => ({
        ...check,
        payment_id: payment.id,
        receipt_number: payment.receipt_number,
        payment_date: payment.payment_date,
        bank_number: payment.bank_number,
        branch_number: payment.branch_number
      }))
    ) || [];

  const getStatusBadge = (status) => {
    const statusColors = {
      pending: 'bg-yellow-100 text-yellow-700',
      cleared: 'bg-green-100 text-green-700',
      bounced: 'bg-red-100 text-red-700',
      cancelled: 'bg-gray-100 text-gray-700'
    };

    const statusLabels = {
      pending: 'ממתין',
      cleared: 'פרוע',
      bounced: 'חזר',
      cancelled: 'מבוטל'
    };

    return (
      <span className={`px-2 py-1 text-xs rounded-full ${statusColors[status]}`}>
        {statusLabels[status]}
      </span>
    );
  };

  const formatCurrency = (amount) => {
    return new Intl.NumberFormat('he-IL', {
      style: 'currency',
      currency: 'ILS'
    }).format(amount);
  };

  const formatDate = (dateStr) => {
    return new Date(dateStr).toLocaleDateString('he-IL', {
      day: '2-digit',
      month: '2-digit',
      year: 'numeric'
    });
  };

  const handleStatusChange = async (checkId, newStatus) => {
    try {
      setUpdatingCheckId(checkId);
      await axiosInstance.put(`/api/admin/checks/${checkId}/status`, {
        status: newStatus
      });
      
      toast.success('סטטוס הצ״ק עודכן בהצלחה');
      
      // Trigger parent refresh
      if (onUpdate) {
        onUpdate();
      }
    } catch (error) {
      toast.error('שגיאה בעדכון סטטוס הצ״ק');
    } finally {
      setUpdatingCheckId(null);
    }
  };

  const filteredChecks = allChecks.filter(check => {
    if (filter === 'all') return true;
    return check.status === filter;
  });

  const statusCounts = {
    all: allChecks.length,
    pending: allChecks.filter(c => c.status === 'pending').length,
    cleared: allChecks.filter(c => c.status === 'cleared').length,
    bounced: allChecks.filter(c => c.status === 'bounced').length,
  };

  const totalAmount = filteredChecks.reduce((sum, check) => sum + parseFloat(check.amount), 0);

  if (!payments || payments.length === 0) {
    return (
      <div className="text-center py-12">
        <svg
          className="mx-auto h-12 w-12 text-gray-400"
          fill="none"
          viewBox="0 0 24 24"
          stroke="currentColor"
        >
          <path
            strokeLinecap="round"
            strokeLinejoin="round"
            strokeWidth={2}
            d="M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z"
          />
        </svg>
        <h3 className="mt-2 text-sm font-medium text-gray-900">אין תשלומים</h3>
        <p className="mt-1 text-sm text-gray-500">לא נמצאו תשלומים לחברה זו</p>
      </div>
    );
  }

  if (allChecks.length === 0) {
    return (
      <div className="text-center py-12">
        <svg
          className="mx-auto h-12 w-12 text-gray-400"
          fill="none"
          viewBox="0 0 24 24"
          stroke="currentColor"
        >
          <path
            strokeLinecap="round"
            strokeLinejoin="round"
            strokeWidth={2}
            d="M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z"
          />
        </svg>
        <h3 className="mt-2 text-sm font-medium text-gray-900">אין צ״קים</h3>
        <p className="mt-1 text-sm text-gray-500">לא נמצאו צ״קים בתשלומים של חברה זו</p>
      </div>
    );
  }

  return (
    <div className="space-y-4">
      {/* Summary Cards */}
      <div className="grid grid-cols-2 md:grid-cols-5 gap-4">
        <div className="bg-white rounded-lg border border-gray-200 p-3">
          <div className="text-xs text-gray-600 mb-1">סה״כ צ״קים</div>
          <div className="text-xl font-bold text-gray-900">{statusCounts.all}</div>
        </div>
        <div className="bg-white rounded-lg border border-gray-200 p-3">
          <div className="text-xs text-gray-600 mb-1">ממתינים</div>
          <div className="text-xl font-bold text-yellow-600">{statusCounts.pending}</div>
        </div>
        <div className="bg-white rounded-lg border border-gray-200 p-3">
          <div className="text-xs text-gray-600 mb-1">פרועים</div>
          <div className="text-xl font-bold text-green-600">{statusCounts.cleared}</div>
        </div>
        <div className="bg-white rounded-lg border border-gray-200 p-3">
          <div className="text-xs text-gray-600 mb-1">חזרו</div>
          <div className="text-xl font-bold text-red-600">{statusCounts.bounced}</div>
        </div>
        <div className="bg-white rounded-lg border border-gray-200 p-3">
          <div className="text-xs text-gray-600 mb-1">סה״כ סכום</div>
          <div className="text-lg font-bold text-primary-600">{formatCurrency(totalAmount)}</div>
        </div>
      </div>

      {/* Filters */}
      <div className="flex flex-wrap gap-2">
        <button
          onClick={() => setFilter('all')}
          className={`px-3 py-1.5 text-sm rounded-md transition-colors ${
            filter === 'all'
              ? 'bg-primary-600 text-white'
              : 'bg-gray-200 text-gray-700 hover:bg-gray-300'
          }`}
        >
          הכל ({statusCounts.all})
        </button>
        <button
          onClick={() => setFilter('pending')}
          className={`px-3 py-1.5 text-sm rounded-md transition-colors ${
            filter === 'pending'
              ? 'bg-yellow-600 text-white'
              : 'bg-gray-200 text-gray-700 hover:bg-gray-300'
          }`}
        >
          ממתינים ({statusCounts.pending})
        </button>
        <button
          onClick={() => setFilter('cleared')}
          className={`px-3 py-1.5 text-sm rounded-md transition-colors ${
            filter === 'cleared'
              ? 'bg-green-600 text-white'
              : 'bg-gray-200 text-gray-700 hover:bg-gray-300'
          }`}
        >
          פרועים ({statusCounts.cleared})
        </button>
        <button
          onClick={() => setFilter('bounced')}
          className={`px-3 py-1.5 text-sm rounded-md transition-colors ${
            filter === 'bounced'
              ? 'bg-red-600 text-white'
              : 'bg-gray-200 text-gray-700 hover:bg-gray-300'
          }`}
        >
          חזרו ({statusCounts.bounced})
        </button>
      </div>

      {/* Checks Table */}
      <div className="bg-white rounded-lg border border-gray-200 overflow-hidden">
        <div className="overflow-x-auto">
          <table className="min-w-full divide-y divide-gray-200">
            <thead className="bg-gray-50">
              <tr>
                <th className="px-4 py-3 text-right text-xs font-medium text-gray-500 uppercase">
                  מס׳ צ״ק
                </th>
                <th className="px-4 py-3 text-right text-xs font-medium text-gray-500 uppercase">
                  תאריך צ״ק
                </th>
                <th className="px-4 py-3 text-right text-xs font-medium text-gray-500 uppercase">
                  סכום
                </th>
                <th className="px-4 py-3 text-right text-xs font-medium text-gray-500 uppercase">
                  בנק/סניף
                </th>
                <th className="px-4 py-3 text-right text-xs font-medium text-gray-500 uppercase">
                  סטטוס
                </th>
                <th className="px-4 py-3 text-right text-xs font-medium text-gray-500 uppercase">
                  עדכון סטטוס
                </th>
                <th className="px-4 py-3 text-right text-xs font-medium text-gray-500 uppercase">
                  קבלה
                </th>
                <th className="px-4 py-3 text-right text-xs font-medium text-gray-500 uppercase">
                  הערות
                </th>
              </tr>
            </thead>
            <tbody className="bg-white divide-y divide-gray-200">
              {filteredChecks.length === 0 ? (
                <tr>
                  <td colSpan="8" className="text-center py-8 text-gray-500">
                    אין צ״קים במצב זה
                  </td>
                </tr>
              ) : (
                filteredChecks.map((check, idx) => (
                  <tr key={check.id || idx} className="hover:bg-gray-50">
                    <td className="px-4 py-3 text-sm font-medium text-gray-900">
                      {check.check_number}
                    </td>
                    <td className="px-4 py-3 text-sm text-gray-700">
                      {formatDate(check.check_date)}
                    </td>
                    <td className="px-4 py-3 text-sm font-bold text-green-600">
                      {formatCurrency(check.amount)}
                    </td>
                    <td className="px-4 py-3 text-sm text-gray-700">
                      {check.bank_number} / {check.branch_number}
                    </td>
                    <td className="px-4 py-3">
                      {getStatusBadge(check.status)}
                    </td>
                    <td className="px-4 py-3">
                      <select
                        value={check.status}
                        onChange={(e) => handleStatusChange(check.id, e.target.value)}
                        disabled={updatingCheckId === check.id}
                        className={`text-sm border border-gray-300 rounded-md px-2 py-1 focus:outline-none focus:ring-2 focus:ring-primary-500 ${
                          updatingCheckId === check.id ? 'opacity-50 cursor-wait' : 'cursor-pointer'
                        }`}
                      >
                        <option value="pending">ממתין</option>
                        <option value="cleared">פרוע</option>
                        <option value="bounced">חזר</option>
                        <option value="cancelled">בוטל</option>
                      </select>
                    </td>
                    <td className="px-4 py-3 text-sm text-gray-600">
                      {check.receipt_number}
                    </td>
                    <td className="px-4 py-3 text-sm text-gray-500">
                      <div className="max-w-xs truncate" title={check.notes || '-'}>
                        {check.notes || '-'}
                      </div>
                    </td>
                  </tr>
                ))
              )}
            </tbody>
            {filteredChecks.length > 0 && (
              <tfoot className="bg-gray-50">
                <tr>
                  <td colSpan="2" className="px-4 py-3 text-sm font-bold text-gray-700">
                    סה״כ ({filteredChecks.length} צ״קים)
                  </td>
                  <td className="px-4 py-3 text-sm font-bold text-green-600">
                    {formatCurrency(totalAmount)}
                  </td>
                  <td colSpan="5"></td>
                </tr>
              </tfoot>
            )}
          </table>
        </div>
      </div>
    </div>
  );
};

export default CompanyChecksList;
